// apiFetch: the one interceptor (Frontend §3.2). Every API request in all three apps goes through here; it is the
// orval mutator of every generated hook. It never logs request or response bodies: they can hold patient data or
// temporary passwords.
import type { ZodType } from 'zod'
import { getApiConfig } from './configure'
import { ApiError, type ApiErrorCode, type ApiIssue } from './errors'
import { ErrorCode } from './generated/zod'

export interface ApiRequestInit extends RequestInit {
  /** Overrides `defaultTimeoutMs` (lookup 20 000, booking 30 000). */
  timeoutMs?: number
  /** The operation's generated zod response schema (orval `includeZodSchemaInArguments`). */
  schema?: ZodType
}

/** orval: the error type of every generated hook. */
export type ErrorType<_Body> = ApiError
/** orval: request bodies are sent as generated. */
export type BodyType<Body> = Body

/** Codes that send the staff app through its guards (step 8). */
const ACCESS_ERROR_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'password_change_required',
  'impersonation_ended',
  'clinic_inactive',
  'no_access',
])

let lastId: string | undefined

/** The `X-Request-Id` of the last response, for staff error toasts ("Ref: …"). */
export const lastRequestId = (): string | undefined => lastId

/** Tests only. */
export function resetLastRequestId(): void {
  lastId = undefined
}

const isPublicPath = (path: string) => path.startsWith('/v1/public/')

/**
 * API.md §1.6 "Patient fallback": a response without a JSON `error` (for example Cloud Armor's 429) gets its code
 * from the status. Statuses the fallback table doesn't name keep the nearest generic code.
 */
function codeFromStatus(status: number, path: string): ApiErrorCode {
  if (status === 401) return isPublicPath(path) ? 'session_expired' : 'unauthorized'
  if (status === 403) return 'blocked'
  if (status === 409) return 'slot_taken'
  if (status === 429) return 'rate_limited'
  if (status === 400) return 'invalid_request'
  if (status === 404) return 'not_found'
  return 'internal_error'
}

/** `Retry-After` as seconds: delta-seconds or an HTTP date. */
function parseRetryAfter(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed)
  const at = Date.parse(trimmed)
  if (Number.isNaN(at)) return undefined
  return Math.max(0, Math.ceil((at - Date.now()) / 1000))
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

function parseIssues(details: Record<string, unknown> | undefined): ApiIssue[] | undefined {
  const raw = details?.issues
  if (!Array.isArray(raw)) return undefined
  return raw.filter(
    (i): i is ApiIssue =>
      isRecord(i) && Array.isArray(i.path) && typeof i.code === 'string' && typeof i.message === 'string',
  )
}

async function readText(res: Response): Promise<string> {
  try {
    return await res.text()
  } catch {
    return ''
  }
}

function parseJson(text: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(text) as unknown }
  } catch {
    return { ok: false }
  }
}

/** Step 6: a non-2xx response → ApiError. */
async function errorFromResponse(res: Response, path: string, requestId: string | undefined): Promise<ApiError> {
  const retryAfterSec = parseRetryAfter(res.headers.get('Retry-After'))
  const parsed = parseJson(await readText(res))
  const body = parsed.ok && isRecord(parsed.value) ? parsed.value : undefined
  const known = body ? ErrorCode.safeParse(body.error) : undefined
  if (body && known?.success) {
    const details = isRecord(body.details) ? body.details : undefined
    return new ApiError({
      status: res.status,
      code: known.data,
      ...(typeof body.message === 'string' && { safeMessage: body.message }),
      ...(details && { details }),
      ...(details && parseIssues(details) && { issues: parseIssues(details) }),
      ...(retryAfterSec !== undefined && { retryAfterSec }),
      ...(requestId !== undefined && { requestId }),
    })
  }
  return new ApiError({
    status: res.status,
    code: codeFromStatus(res.status, path),
    ...(retryAfterSec !== undefined && { retryAfterSec }),
    ...(requestId !== undefined && { requestId }),
  })
}

/** Steps 1–5: one request with headers, timeout and transport-error mapping. */
async function send(url: string, path: string, init: RequestInit, timeoutMs: number, forceRefresh: boolean) {
  const config = getApiConfig()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body !== undefined && init.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (config.getIdToken) {
    const token = await config.getIdToken(forceRefresh)
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }
  const impersonationId = config.getImpersonationId?.(path)
  if (impersonationId) headers.set('X-Impersonation-Id', impersonationId)

  // Step 3. Equivalent to AbortSignal.any([init.signal, AbortSignal.timeout(timeoutMs)]); a plain timer so the
  // tests' fake timers drive it.
  const timeout = new AbortController()
  const timer = setTimeout(() => {
    timeout.abort(new DOMException('The request timed out', 'TimeoutError'))
  }, timeoutMs)
  const signal = init.signal ? AbortSignal.any([init.signal, timeout.signal]) : timeout.signal

  try {
    return await fetch(url, { ...init, headers, credentials: config.credentials, signal })
  } catch (e) {
    if (timeout.signal.aborted) throw new ApiError({ status: 0, code: 'timeout' })
    // The caller cancelled (for example TanStack Query on unmount): pass the abort through untouched.
    if (init.signal?.aborted) throw e
    throw new ApiError({ status: 0, code: 'network_error' })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * The orval mutator. Resolves with the parsed body (`undefined` for 204) or rejects with an {@link ApiError}.
 * `url` is the API path with its query string, for example `/v1/clinic/clinics/c1/appointments?page=2`.
 */
export async function apiFetch<T>(url: string, init: ApiRequestInit = {}): Promise<T> {
  const config = getApiConfig()
  const { timeoutMs = config.defaultTimeoutMs, schema, ...requestInit } = init
  const path = url.split('?')[0] ?? url
  const fullUrl = `${config.baseUrl.replace(/\/+$/, '')}${url}`

  let res = await send(fullUrl, path, requestInit, timeoutMs, false)
  let requestId = res.headers.get('X-Request-Id') ?? undefined
  if (requestId !== undefined) lastId = requestId

  if (!res.ok) {
    let error = await errorFromResponse(res, path, requestId)

    // Step 7. Staff: one forced token refresh and one replay. Safe for mutations too: a 401 means the request was
    // rejected at the first gate.
    if (error.code === 'unauthorized' && config.getIdToken) {
      res = await send(fullUrl, path, requestInit, timeoutMs, true)
      requestId = res.headers.get('X-Request-Id') ?? undefined
      if (requestId !== undefined) lastId = requestId
      if (res.ok) return readSuccess(res, schema, config.validateResponses, requestId)
      error = await errorFromResponse(res, path, requestId)
      if (error.code === 'unauthorized') config.onAuthFailure?.(error)
    }

    // Step 8. Staff: guard redirects.
    if (error.status === 403 && ACCESS_ERROR_CODES.has(error.code)) config.onAccessError?.(error)
    throw error
  }

  return readSuccess(res, schema, config.validateResponses, requestId)
}

/** Steps 9–10: parse a 2xx body, validate it outside prod, 204 → undefined. */
async function readSuccess<T>(
  res: Response,
  schema: ZodType | undefined,
  validate: boolean,
  requestId: string | undefined,
): Promise<T> {
  const text = res.status === 204 || res.status === 205 ? '' : await readText(res)
  if (text === '') return undefined as T
  const parsed = parseJson(text)
  const invalid = (issues?: ApiIssue[]) =>
    new ApiError({
      status: res.status,
      code: 'invalid_response',
      ...(issues && { issues }),
      ...(requestId !== undefined && { requestId }),
    })
  if (!parsed.ok) throw invalid()
  if (validate && schema) {
    const result = schema.safeParse(parsed.value)
    if (!result.success) {
      // Paths and codes only: zod messages are generic, and the received values never leave this function.
      throw invalid(
        result.error.issues.map((i) => ({
          path: i.path.filter((p): p is string | number => typeof p !== 'symbol'),
          code: i.code,
          message: i.message,
        })),
      )
    }
  }
  // The body as received: validation never rewrites it, so local and prod see the same data.
  return parsed.value as T
}
